import { useEffect, useRef, useState } from "react";
import Button from "@ingka/button";
import styles from "./DateRangeModal.module.css";

interface DateRangeModalProps {
    initialStart?: string;
    initialEnd?: string;
    /** Earliest allowed date (overrides default 2025-09-01 if provided) */
    minAllowedDate?: string;
    onApply: (start: string, end: string) => void;
    onCancel: () => void;
}

/** Earliest allowed start date — data only available from FY26 (Sep 2025). */
const DEFAULT_MIN_DATE = "2025-09-01";

function getMinDate(override?: string): string {
    return override || DEFAULT_MIN_DATE;
}

/** Latest allowed end for custom range: yesterday (data available up to today exclusive). */
function getMaxDate(): string {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    return toISO(yesterday);
}

function toISO(d: Date): string {
    return d.toISOString().slice(0, 10);
}

/** Default start: 8 weeks before the max date's week start. */
function getDefaultStart(): string {
    const max = new Date(getMaxDate());
    // Find the Sunday of max's week
    const day = max.getDay();
    const weekStart = new Date(max);
    weekStart.setDate(max.getDate() - day);
    // Go back 8 weeks
    weekStart.setDate(weekStart.getDate() - 8 * 7);
    return toISO(weekStart);
}

export function DateRangeModal({ initialStart, initialEnd, minAllowedDate, onApply, onCancel }: DateRangeModalProps) {
    const minDate = getMinDate(minAllowedDate);
    const maxDate = getMaxDate();
    const [startDate, setStartDate] = useState(initialStart || getDefaultStart());
    const [endDate, setEndDate] = useState(initialEnd || maxDate);
    const [error, setError] = useState("");
    const overlayRef = useRef<HTMLDivElement>(null);

    // Validate on every change
    useEffect(() => {
        if (!startDate || !endDate) {
            setError("");
            return;
        }
        if (startDate > endDate) {
            setError("Start date must be before end date");
        } else if (startDate < minDate) {
            setError(`Data is only available from ${minDate}`);
        } else if (endDate > maxDate) {
            setError("End date cannot be in the future");
        } else if (startDate === endDate) {
            setError("Date range must span at least one day");
        } else {
            setError("");
        }
    }, [startDate, endDate, minDate, maxDate]);

    // Close on Escape
    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onCancel();
        };
        document.addEventListener("keydown", handleKey);
        return () => document.removeEventListener("keydown", handleKey);
    }, [onCancel]);

    const handleOverlayClick = (e: React.MouseEvent) => {
        if (e.target === overlayRef.current) onCancel();
    };

    const isValid = startDate && endDate && !error;

    return (
        <div className={styles.overlay} ref={overlayRef} onClick={handleOverlayClick}>
            <div className={styles.modal}>
                <h3 className={styles.title}>Custom Date Range</h3>
                <div className={styles.fields}>
                    <div className={styles.field}>
                        <label className={styles.label}>Start date</label>
                        <input
                            type="date"
                            className={styles.input}
                            value={startDate}
                            min={minDate}
                            max={endDate || maxDate}
                            title={`Earliest: ${minDate} (data available from FY26)`}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className={styles.field}>
                        <label className={styles.label}>End date</label>
                        <input
                            type="date"
                            className={styles.input}
                            value={endDate}
                            min={startDate || minDate}
                            max={maxDate}
                            title={`Latest: ${maxDate} (yesterday)`}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                </div>
                <div className={styles.hint}>Earliest: {minDate} · latest yesterday</div>
                {error && <div className={styles.error}>{error}</div>}
                <div className={styles.buttons}>
                    <Button text="Cancel" type="secondary" size="xsmall" onClick={onCancel} />
                    <Button
                        text="Apply"
                        type="emphasised"
                        size="xsmall"
                        disabled={!isValid}
                        onClick={() => isValid && onApply(startDate, endDate)}
                    />
                </div>
            </div>
        </div>
    );
}
